/**
 * APEX v7.7.1 — Game-level Insights : Catégorie A (morts contextualisées)
 *
 * Patterns détectés (référence PRD section 13.2) :
 *   - A1 : Mort pré-objectif (nécessite timeline objectifs — décalé v7.7.2)
 *   - A2 : Mort en outnumbered (nécessite morts alliés — décalé v7.7.2)
 *   - A3 : Mort post-recall (fenêtre 1 min — décalé v7.7.2 : nécessite events recall)
 *   - A4 : Mort avec jungler dead (nécessite morts alliés — décalé v7.7.2)
 *   - A5 : Cluster de morts ✅ FAISABLE MAINTENANT (proximité spatiale + temporelle)
 *   - A6 : Mort solo deep ✅ FAISABLE MAINTENANT (position deep + heuristique)
 *   - A8 : Mort par burst (Tier 2 — inline contextuel, pas dans Coach Header)
 *      ❌ Nécessite la durée entre 1er dégât et mort, pas dans nos données → décalé
 *
 * Pour v7.7.1, on livre A5 et A6 qui s'appuient uniquement sur deathDetails existants.
 * Les autres patterns nécessitent une extension du parser Riot (à faire en v7.7.2).
 */

const RIFT_WIDTH = 14820;   // coordonnée map LoL standard
const MAP_CENTER = RIFT_WIDTH / 2;

/**
 * Distance euclidienne entre deux positions {x, y}.
 */
function distance(p1, p2) {
  if (!p1 || !p2) return Infinity;
  const dx = (p1.x || 0) - (p2.x || 0);
  const dy = (p1.y || 0) - (p2.y || 0);
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Convertit minute/second d'une mort en secondes totales.
 */
function deathTotalSeconds(death) {
  return (death.minute || 0) * 60 + (death.second || 0);
}

/**
 * Détermine si on joue côté blue (côté gauche bas).
 */
function isBlueSide(game) {
  return game.mySide === 'blue';
}

/**
 * Une position est-elle "deep" en territoire ennemi ?
 * Heuristique : côté blue → deep = position avec y > 11000 ou x > 11000
 *               côté red → deep = position avec y < 4000 ou x < 4000
 */
function isDeepPosition(position, blueSide) {
  if (!position) return false;
  if (blueSide) {
    return (position.y > 11000) || (position.x > 11000);
  } else {
    return (position.y < 4000) || (position.x < 4000);
  }
}

// ============================================================
// A5 — Cluster de morts
// ============================================================

/**
 * Détecte des clusters : 2+ morts dans les 90s sur des positions proches (<3000 unités).
 * Retourne un insight si au moins 1 cluster est trouvé.
 */
function detectClusterDeaths(deaths) {
  if (!Array.isArray(deaths) || deaths.length < 2) return null;

  // Tri par temps
  const sorted = [...deaths].sort((a, b) => deathTotalSeconds(a) - deathTotalSeconds(b));

  for (let i = 0; i < sorted.length - 1; i++) {
    const d1 = sorted[i];
    for (let j = i + 1; j < sorted.length; j++) {
      const d2 = sorted[j];
      const deltaT = deathTotalSeconds(d2) - deathTotalSeconds(d1);
      if (deltaT > 90) break; // trop éloignés temporellement

      const dist = distance(d1.position, d2.position);
      if (dist < 3000) {
        // Cluster détecté
        const m1 = d1.minute;
        const m2 = d2.minute;
        return {
          id: 'A5_cluster_deaths',
          category: 'A',
          severity: 'warning',
          title: 'Cluster de morts',
          short: `2 morts au même endroit en ${deltaT}s`,
          long: `Tu es mort à ${m1}min puis ${m2}min sur des positions proches. Tu reviens dans la même zone après avoir die — pattern à briser.`,
          context: { delta_seconds: deltaT, distance_units: Math.round(dist) }
        };
      }
    }
  }

  return null;
}

// ============================================================
// A6 — Mort solo deep
// ============================================================

/**
 * Détecte une mort en position deep ennemi.
 * Heuristique simplifiée (sans positions alliées) : on flag si position deep ET mort
 * tagged 'greed' ou 'position' à ≥10min ingame (pas en early lane phase normale).
 *
 * NOTE : la version complète du pattern A6 (avec check "0 allié dans rayon 4000") nécessite
 * les positions alliées au timestamp de la mort, qui ne sont pas dans nos données actuelles.
 * En v7.7.1 on livre la version heuristique.
 */
function detectDeepSoloDeath(deaths, game) {
  if (!Array.isArray(deaths) || deaths.length === 0) return null;
  const blueSide = isBlueSide(game);

  const deepDeaths = deaths.filter(d => {
    if (d.minute < 10) return false;
    if (d.type !== 'greed' && d.type !== 'position') return false;
    return isDeepPosition(d.position, blueSide);
  });

  if (deepDeaths.length === 0) return null;

  // Garder la pire (la plus tard ou plus deep)
  const worst = deepDeaths[0];

  return {
    id: 'A6_deep_solo_death',
    category: 'A',
    severity: 'warning',
    title: 'Mort en territoire ennemi',
    short: `Mort deep à ${worst.minute}min`,
    long: `Tu es mort en position avancée en territoire ennemi à ${worst.minute}:${String(worst.second).padStart(2,'0')} — position trop greedy pour un ADC sans backup.`,
    context: { minute: worst.minute, position: worst.position }
  };
}

// ============================================================
// A8 — Mort par burst (tier 2, inline uniquement)
// ============================================================
//
// Pas livré en v7.7.1 : nécessite la "durée entre 1er dégât reçu et 0HP" qui n'est
// pas dans nos données actuelles. À implémenter en étendant le parser pour récupérer
// cette info dans la timeline Riot (CHAMPION_KILL event a un timestamp précis, on peut
// rétroactivement chercher le 1er CHAMPION_DAMAGE event dans la fenêtre précédente).

// ============================================================
// A1 — Mort pré-objectif (v7.7.2 — débloqué via objectiveEvents)
// ============================================================

/**
 * Détecte une mort <30s avant qu'un objectif tombe pour l'équipe ennemie.
 * Liste objectifs 2026 : Dragon, Baron, Rift Herald, Voidgrubs (Atakhan retiré patch 26.1).
 */
function detectPreObjectiveDeath(game) {
  const deaths = game.deathDetails || [];
  const objEvents = game.objectiveEvents || [];
  if (deaths.length === 0 || objEvents.length === 0) return null;

  // Objectifs majeurs pris par l'ennemi
  const enemyObjectives = objEvents.filter(e =>
    e.type === 'monster' && !e.isMyTeam
  );
  if (enemyObjectives.length === 0) return null;

  for (const death of deaths) {
    const deathSec = (death.minute || 0) * 60 + (death.second || 0);
    for (const obj of enemyObjectives) {
      const objSec = (obj.minute || 0) * 60 + (obj.second || 0);
      const gap = objSec - deathSec;
      // Mort 0-30s avant l'objectif ennemi
      if (gap >= 0 && gap <= 30) {
        const objLabel = labelObjective(obj.subType);
        return {
          id: 'A1_pre_objective_death',
          category: 'A',
          severity: 'critical',
          title: 'Mort pré-objectif',
          short: `Mort ${gap}s avant ${objLabel} ennemi`,
          long: `Tu es mort à ${death.minute}:${String(death.second).padStart(2,'0')}, et ${gap}s plus tard l'équipe ennemie a pris ${objLabel}. Ta mort a ouvert l'objectif.`,
          context: { gap_seconds: gap, objective: obj.subType }
        };
      }
    }
  }
  return null;
}

function labelObjective(subType) {
  const map = {
    DRAGON: 'le Dragon',
    RIFTHERALD: 'le Rift Herald',
    BARON_NASHOR: 'le Baron',
    HORDE: 'les Voidgrubs',
    ELDER_DRAGON: 'le Dragon Ancestral'
  };
  return map[subType] || "l'objectif";
}

// ============================================================
// A2 — Mort en outnumbered (v7.7.2 — débloqué via allyDeaths)
// ============================================================

/**
 * Détecte une mort alors que 2+ alliés sont déjà morts dans les 15s précédentes.
 */
function detectOutnumberedDeath(game) {
  const deaths = game.deathDetails || [];
  const allyDeaths = game.allyDeaths || [];
  if (deaths.length === 0 || allyDeaths.length === 0) return null;

  for (const death of deaths) {
    const deathSec = (death.minute || 0) * 60 + (death.second || 0);
    // Compter les alliés morts dans la fenêtre [-15s, 0]
    const recentAllyDeaths = allyDeaths.filter(ad => {
      const adSec = (ad.minute || 0) * 60 + (ad.second || 0);
      return adSec >= deathSec - 15 && adSec < deathSec;
    });
    if (recentAllyDeaths.length >= 2) {
      return {
        id: 'A2_outnumbered_death',
        category: 'A',
        severity: 'warning',
        title: 'Mort en infériorité',
        short: `${recentAllyDeaths.length} alliés down avant ta mort`,
        long: `À ${death.minute}:${String(death.second).padStart(2,'0')}, ${recentAllyDeaths.length} alliés étaient déjà morts dans les 15s avant toi. Tu as engagé/suivi un fight déjà perdu.`,
        context: { allies_down: recentAllyDeaths.length, minute: death.minute }
      };
    }
  }
  return null;
}

// ============================================================
// A4 — Mort avec jungler dead (v7.7.2 — garde-fou early game)
// ============================================================

/**
 * Détecte une mort alors que ton jungler est mort dans les 20s avant.
 * Garde-fou : exclure les morts < 8 min ingame (early game = trop de bruit).
 */
function detectJunglerDeadDeath(game) {
  const deaths = game.deathDetails || [];
  const allyDeaths = game.allyDeaths || [];
  if (deaths.length === 0 || allyDeaths.length === 0) return null;

  const junglerDeaths = allyDeaths.filter(ad => ad.isJungler);
  if (junglerDeaths.length === 0) return null;

  for (const death of deaths) {
    if (death.minute < 8) continue; // garde-fou early game
    const deathSec = death.minute * 60 + (death.second || 0);
    const jgDeadRecent = junglerDeaths.some(jd => {
      const jdSec = (jd.minute || 0) * 60 + (jd.second || 0);
      return jdSec >= deathSec - 20 && jdSec < deathSec;
    });
    if (jgDeadRecent) {
      return {
        id: 'A4_jungler_dead_death',
        category: 'A',
        severity: 'warning',
        title: 'Mort jungler down',
        short: `Mort à ${death.minute}min, jungler dead avant`,
        long: `À ${death.minute}:${String(death.second).padStart(2,'0')}, ton jungler était mort dans les 20s avant toi. Tu n'as pas réagi à l'info "jungler down" — tu aurais dû jouer plus safe.`,
        context: { minute: death.minute }
      };
    }
  }
  return null;
}



/**
 * Insight de synthèse : ratio greed/position sur la game vs la baseline user.
 * Comparaison à TA baseline (différenciant APEX vs OP.GG).
 */
function detectGreedSpikeOnGame(game, context) {
  const deaths = game.deathDetails || [];
  const greedCount = deaths.filter(d => d?.type === 'greed').length;

  // Pas de pattern si pas de greed
  if (greedCount === 0) return null;

  // Si l'user a une baseline, on compare ; sinon on flag à partir de 3+ greed
  const baseline = context?.userStats?.avgGreedPerGame;

  if (typeof baseline === 'number' && baseline > 0) {
    if (greedCount >= baseline * 2 && greedCount >= 3) {
      return {
        id: 'A_greed_spike_vs_baseline',
        category: 'A',
        severity: 'critical',
        title: 'Pic de greed deaths',
        short: `${greedCount} greed deaths (ta moyenne = ${baseline.toFixed(1)})`,
        long: `Cette game a ${greedCount} greed deaths, soit 2x+ ta moyenne habituelle de ${baseline.toFixed(1)}/game. Game à oublier — focus sur le pattern qui a causé ça.`,
        context: { greed_count: greedCount, baseline }
      };
    }
  } else if (greedCount >= 3) {
    return {
      id: 'A_high_greed_count',
      category: 'A',
      severity: 'warning',
      title: 'Beaucoup de greed deaths',
      short: `${greedCount} greed deaths sur la game`,
      long: `Cette game contient ${greedCount} morts catégorisées greed. Identifie le pattern qui se répète.`,
      context: { greed_count: greedCount }
    };
  }

  return null;
}

/**
 * Insight positif : aucune greed death sur la game.
 */
function detectCleanGame(game) {
  const deaths = game.deathDetails || [];
  const totalDeaths = deaths.length;
  const greedCount = deaths.filter(d => d?.type === 'greed').length;

  // Clean game = 0 greed ET ≤ 3 deaths total
  if (greedCount === 0 && totalDeaths <= 3 && totalDeaths > 0) {
    return {
      id: 'A_clean_game',
      category: 'A',
      severity: 'positive',
      title: 'Game discipliné',
      short: `${totalDeaths} death${totalDeaths > 1 ? 's' : ''}, aucune greed`,
      long: `Game maîtrisée : ${totalDeaths} mort${totalDeaths > 1 ? 's' : ''} et 0 greed death. Le pattern à reproduire.`,
      context: { total_deaths: totalDeaths, greed_count: 0 }
    };
  }
  return null;
}

/**
 * Insight positif : 0 death sur la game (rare !).
 */
function detectDeathless(game) {
  const deaths = game.deathDetails || [];
  if (deaths.length === 0 && game.deaths === 0) {
    return {
      id: 'A_deathless_game',
      category: 'A',
      severity: 'positive',
      title: 'Sans mourir 🔥',
      short: '0 death sur la game',
      long: 'Game irréprochable côté survie. Le standard à viser.',
      context: { total_deaths: 0 }
    };
  }
  return null;
}

// ============================================================
// API publique
// ============================================================

/**
 * Analyse les morts d'une game et retourne tous les insights de catégorie A.
 */
export function analyzeDeaths(game, context = {}) {
  if (!game) return [];

  const deaths = Array.isArray(game.deathDetails) ? game.deathDetails : [];
  const insights = [];

  // Si la game est sans mort
  const deathless = detectDeathless(game);
  if (deathless) insights.push(deathless);

  // Sinon, on cherche patterns
  if (deaths.length > 0) {
    // A1 — mort pré-objectif (v7.7.2)
    const preObj = detectPreObjectiveDeath(game);
    if (preObj) insights.push(preObj);

    // A2 — outnumbered (v7.7.2)
    const outnumbered = detectOutnumberedDeath(game);
    if (outnumbered) insights.push(outnumbered);

    // A4 — jungler dead (v7.7.2)
    const jglDead = detectJunglerDeadDeath(game);
    if (jglDead) insights.push(jglDead);

    // A5 — cluster
    const cluster = detectClusterDeaths(deaths);
    if (cluster) insights.push(cluster);

    // A6 — deep solo
    const deepSolo = detectDeepSoloDeath(deaths, game);
    if (deepSolo) insights.push(deepSolo);

    // Synthèse haut niveau
    const greedSpike = detectGreedSpikeOnGame(game, context);
    if (greedSpike) insights.push(greedSpike);

    const cleanGame = detectCleanGame(game);
    if (cleanGame) insights.push(cleanGame);
  }

  return insights;
}
